import AttachMoneyIcon from '@material-design-icons/svg/filled/attach_money.svg?react';
import { useWallet } from '@solana/wallet-adapter-react';
import React, { use } from 'react';
import { TRPCContext } from '../../../contexts/TRPC';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../ui/dialog';
import { LoadingSpinner } from '../../ui/LoadingSpinner';
import type { ProviderConfigSectionProps } from './types';
import { useArDriveBalance } from './useArDriveBalance';
import { useArDrivePendingRecovery } from './useArDrivePendingRecovery';
import { useArDriveTopUp } from './useArDriveTopUp';

const formatArValue = (value: string | null, arToUsdRate: number | null) => {
  const parsed = Number.parseFloat(value ?? '0');
  const current = Number.isFinite(parsed) ? parsed : 0;
  let usdText = '';
  if (arToUsdRate && arToUsdRate > 0 && current > 0) {
    const usdValue = current * arToUsdRate;
    const formattedUsd =
      usdValue < 0.01 ? usdValue.toFixed(4) : usdValue.toFixed(2);
    usdText = ` ($${formattedUsd})`;
  }

  return `${current.toFixed(4)} AR${usdText}`;
};

export const ArDriveConfigSection: React.FC<ProviderConfigSectionProps> = ({
  isSelected,
}) => {
  const wallet = useWallet();
  const trpc = use(TRPCContext);
  const {
    ardriveBalance,
    fileSize,
    setFileSize,
    estimatedCost,
    remainingArDriveBalance,
    arToUsdRate,
    setArToUsdRate,
    checkArDriveBalance,
  } = useArDriveBalance({ isSelected, wallet });
  useArDrivePendingRecovery({
    isSelected,
    wallet,
    trpc,
    checkArDriveBalance,
  });
  const {
    showArDriveTopUpModal,
    setShowArDriveTopUpModal,
    topUpAmount,
    setTopUpAmount,
    isToppingUp,
    solToUsdRate,
    handleArDriveTopUp,
  } = useArDriveTopUp({
    wallet,
    trpc,
    arToUsdRate,
    setArToUsdRate,
    checkArDriveBalance,
  });

  return (
    <>
      <div className="pt-4 mt-3 border-t border-neutral-700">
        <div className="pb-4 space-y-3">
          <div className="bg-neutral-900/50 rounded-lg p-3">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <AttachMoneyIcon className="size-4 fill-neutral-400" />
                <span className="text-sm font-medium text-neutral-300">
                  Cost Estimator: {fileSize} MB
                </span>
              </div>
              <button
                type="button"
                onClick={() => setShowArDriveTopUpModal(true)}
                className="!text-xs border border-teal-600 text-teal-600 px-3 py-1 rounded hover:bg-teal-600/10 font-medium"
              >
                Top Up Balance
              </button>
            </div>
            <div className="space-y-2">
              <div className="pt-6">
                <input
                  type="range"
                  aria-label="File size in megabytes"
                  min={1}
                  max={500}
                  step={1}
                  value={fileSize}
                  onChange={(event) =>
                    setFileSize(event.currentTarget.valueAsNumber)
                  }
                  className="w-full accent-blue-500"
                />
                <div className="flex justify-between text-xs text-neutral-500 mt-1">
                  <span>1 MB</span>
                  <span>500 MB</span>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-2 text-xs">
                <div className="text-center">
                  <p className="text-neutral-500">Current</p>
                  <span className="font-mono text-green-400 h-4 flex items-center justify-center">
                    {formatArValue(ardriveBalance, arToUsdRate)}
                  </span>
                </div>
                <div className="text-center">
                  <p className="text-neutral-500">Cost</p>
                  <span className="font-mono text-yellow-400 h-4 flex items-center justify-center">
                    {formatArValue(estimatedCost, arToUsdRate)}
                  </span>
                </div>
                <div className="text-center">
                  <p className="text-neutral-500">Remaining</p>
                  <span
                    className={`font-mono h-4 flex items-center justify-center ${parseFloat(remainingArDriveBalance || ardriveBalance || '0') < 0 ? 'text-red-400' : 'text-green-400'}`}
                  >
                    {formatArValue(
                      remainingArDriveBalance || ardriveBalance,
                      arToUsdRate,
                    )}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Dialog
        open={showArDriveTopUpModal}
        onOpenChange={setShowArDriveTopUpModal}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Top Up ArDrive Balance</DialogTitle>
            <DialogDescription>
              Purchase ArDrive Turbo credits with SOL. The transaction will be
              signed with your connected wallet and sent to the Turbo service.
            </DialogDescription>
          </DialogHeader>

          <DialogBody>
            <div className="space-y-4">
              <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-3">
                <div className="flex gap-2">
                  <div className="text-blue-400 mt-0.5">
                    <svg
                      className="size-4"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      aria-label="Info"
                    >
                      <title>Info</title>
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                  </div>
                  <div className="text-xs text-neutral-300 space-y-1">
                    <p className="font-medium text-blue-400">
                      About Turbo Credits
                    </p>
                    <p>
                      Turbo credits are not blockchain tokens and cannot be
                      transferred, traded, or sold. They are non-refundable
                      storage credits specific to your wallet address.
                    </p>
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-neutral-300 mb-2">
                  Amount (SOL)
                </label>
                <input
                  type="number"
                  aria-label="Amount (SOL)"
                  value={topUpAmount}
                  onChange={(e) => {
                    setTopUpAmount(e.target.value);
                  }}
                  min="0.005"
                  max="1"
                  step="0.01"
                  className="w-full px-4 py-2 bg-neutral-800 border border-neutral-700 rounded-lg text-white placeholder-neutral-500 focus:outline-none focus:border-blue-500"
                  placeholder="0.01"
                />
                <p className="text-xs text-neutral-500 mt-2">
                  Minimum: 0.005 SOL, Maximum: 1 SOL
                </p>
              </div>

              <div className="bg-neutral-800/50 rounded-lg p-4 space-y-3">
                <h3 className="text-sm font-medium text-neutral-300">
                  Estimated Value
                </h3>

                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-neutral-400">SOL Amount</span>
                    <span className="text-white font-mono">
                      {(parseFloat(topUpAmount) || 0).toFixed(2)} SOL
                    </span>
                  </div>

                  <div className="flex justify-between text-sm">
                    <span className="text-neutral-400">Current AR Rate</span>
                    <span className="text-neutral-300 font-mono">
                      {arToUsdRate
                        ? `1 AR = $${arToUsdRate.toFixed(2)} USD`
                        : 'Loading...'}
                    </span>
                  </div>

                  <div className="flex justify-between text-sm">
                    <span className="text-neutral-400">Estimated Value</span>
                    <span className="text-green-400 font-mono">
                      {solToUsdRate
                        ? `$${(
                            (parseFloat(topUpAmount) || 0) * solToUsdRate
                          ).toFixed(2)} USD`
                        : 'Loading...'}
                    </span>
                  </div>

                  {solToUsdRate && arToUsdRate && (
                    <>
                      <div className="flex justify-between text-sm">
                        <span className="text-neutral-400">Estimated AR</span>
                        <span className="text-green-400 font-mono">
                          ~
                          {formatArValue(
                            (
                              ((parseFloat(topUpAmount) || 0) * solToUsdRate) /
                              arToUsdRate
                            ).toFixed(4),
                            null,
                          )}
                        </span>
                      </div>

                      {estimatedCost && (
                        <div className="flex justify-between text-sm">
                          <span className="text-neutral-400">
                            Estimated Storage
                          </span>
                          <span className="text-blue-400 font-mono">
                            ~
                            {(
                              (((parseFloat(topUpAmount) || 0) * solToUsdRate) /
                                arToUsdRate /
                                parseFloat(estimatedCost)) *
                              fileSize
                            ).toFixed(0)}
                            MB
                          </span>
                        </div>
                      )}
                    </>
                  )}
                </div>

                <div className="pt-2 border-t border-neutral-700">
                  <p className="text-xs text-neutral-500">
                    {solToUsdRate ? (
                      <>
                        {`Current SOL price: $${solToUsdRate.toFixed(2)} USD. `}
                        The exact AR amount depends on exchange rates when
                        processed.
                      </>
                    ) : (
                      'Loading current SOL price...'
                    )}
                  </p>
                </div>
              </div>
            </div>
          </DialogBody>

          <DialogFooter className="gap-3">
            <button
              type="button"
              onClick={() => setShowArDriveTopUpModal(false)}
              disabled={isToppingUp}
              className="bg-neutral-700 text-white font-medium py-2 px-6 rounded-lg hover:bg-neutral-600 disabled:opacity-50 disabled:hover:bg-neutral-700"
              style={{ cursor: isToppingUp ? 'default' : 'pointer' }}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleArDriveTopUp}
              disabled={isToppingUp}
              className="bg-teal-600 text-white font-medium py-2 px-6 rounded-lg hover:bg-teal-700 disabled:opacity-50 disabled:hover:bg-teal-600 relative min-w-[90px] flex items-center justify-center"
              style={{ cursor: isToppingUp ? 'default' : 'pointer' }}
            >
              {isToppingUp ? (
                <LoadingSpinner size="md" className="text-white" />
              ) : (
                'Top Up'
              )}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
